import { initializeApp, type FirebaseApp } from "firebase/app";
import { connectAuthEmulator, getAuth, type Auth } from "firebase/auth";
import { connectFirestoreEmulator, getFirestore, type Firestore } from "firebase/firestore";
import type { RuntimeConfig } from "./runtime-config";

export interface FirebaseClients {
  app: FirebaseApp;
  auth: Auth;
  /** Read-only use: listeners on domain data (architecture §2); writes go through the api. */
  db: Firestore;
}

function splitHostPort(value: string): { host: string; port: number } {
  const at = value.lastIndexOf(":");
  return { host: value.slice(0, at), port: Number(value.slice(at + 1)) };
}

/** Initialises Firebase once from the runtime config, pointing at the emulators when asked. */
export function initFirebase(config: RuntimeConfig): FirebaseClients {
  const { apiKey, authDomain, projectId, firestoreDatabaseId, messagingSenderId, appId } = config.firebase;
  const app = initializeApp({
    apiKey,
    authDomain,
    projectId,
    ...(messagingSenderId === null ? {} : { messagingSenderId }),
    ...(appId === null ? {} : { appId }),
  });
  const auth = getAuth(app);
  const db = getFirestore(app, firestoreDatabaseId);
  if (config.useEmulators) {
    if (config.emulators === undefined) throw new Error("emulators are required when useEmulators is true");
    connectAuthEmulator(auth, `http://${config.emulators.auth}`, { disableWarnings: true });
    const fs = splitHostPort(config.emulators.firestore);
    connectFirestoreEmulator(db, fs.host, fs.port);
  }
  return { app, auth, db };
}
